import React, { useState, useRef, useEffect, useMemo } from 'react';
import VoiceRecorder from './VoiceRecorder';
import useVoiceRecognition from '../../hooks/useVoiceRecognition';
import { isMobileDevice } from '../../utils/speechRecognition';

// Inline SVG icons
const PaperAirplaneIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const GlobeAltIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
  </svg>
);

const LockClosedIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const ClipboardCheckIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

/**
 * Inmatningskomponent för anteckningar med röst och text
 *
 * @param {Object} props
 * @param {function} props.onSubmit - Callback när anteckning skickas (content, inputMethod, voiceConfidence, visibility)
 * @param {boolean} props.disabled - Om inmatning är disabled
 * @param {string} props.placeholder - Placeholder-text
 * @param {boolean} props.autoFocus - Auto-fokus på textfältet
 */
export function NoteInput({
  onSubmit,
  disabled = false,
  placeholder = "Skriv eller diktera... (säg 'stopp' för att avsluta)",
  autoFocus = false
}) {
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState('shared');
  const [isActionPoint, setIsActionPoint] = useState(true);
  const [inputMethod, setInputMethod] = useState('text');
  const [voiceConfidence, setVoiceConfidence] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Rum/kategori — sticky: behåller sitt värde mellan anteckningar
  const [room, setRoom] = useState('');

  const textareaRef = useRef(null);

  // Hjälpfunktion för att konvertera talade skiljetecken till faktiska tecken
  const convertPunctuation = (text) => {
    let result = text;

    // Enklare matchning - byt ut orden oavsett position
    result = result.replace(/\bpunkt\b/gi, '.');
    result = result.replace(/\bkomma\b/gi, ',');
    result = result.replace(/\bbindestreck\b/gi, '-');
    result = result.replace(/\bradbryt\b/gi, '\n');
    result = result.replace(/\bny\s*rad\b/gi, '\n');
    result = result.replace(/\bnyrad\b/gi, '\n');

    // Städa upp extra mellanslag (använd [^\S\n] för "whitespace utom newline")
    result = result.replace(/[^\S\n]*\.[^\S\n]*/g, '. ');
    result = result.replace(/[^\S\n]*,[^\S\n]*/g, ', ');
    result = result.replace(/[^\S\n]*-[^\S\n]*/g, '-');
    // Newline cleanup - ta bort mellanslag runt radbryt men behåll radbryt
    result = result.replace(/[^\S\n]*\n[^\S\n]*/g, '\n');

    // Ta bort mellanslag i början/slutet av varje rad
    result = result.split('\n').map(line => line.trim()).join('\n');

    // Ta bort trailing skiljetecken mellanslag
    result = result.replace(/\.\s*$/g, '.');
    result = result.replace(/,\s*$/g, ',');

    return result;
  };

  // Kolla om vi är på mobil (avgör continuous mode)
  const isMobile = useMemo(() => isMobileDevice(), []);

  // Röstinmatning
  const {
    isListening,
    isSupported,
    interimTranscript,
    error: voiceError,
    startListening,
    stopListening
  } = useVoiceRecognition({
    continuous: !isMobile,  // Desktop: continuous, Mobil: en fras i taget
    onResult: (text, confidence) => {
      const lowerText = text.toLowerCase().trim();

      // Kommando: "stopp" - avsluta inspelningen (endast relevant på desktop med continuous)
      if (lowerText === 'stopp' || lowerText.endsWith(' stopp')) {
        const cleanedText = convertPunctuation(text.replace(/\s*stopp\s*$/gi, '').trim());
        if (cleanedText) {
          setContent(prev => prev ? `${prev} ${cleanedText}` : cleanedText);
        }
        setInputMethod('voice');
        setVoiceConfidence(confidence);
        if (!isMobile) {
          setTimeout(() => stopListening(), 50);
        }
        return;
      }

      // Konvertera skiljetecken och LÄGG TILL (append) till befintlig text
      const processedText = convertPunctuation(text);
      if (!processedText) return;

      setContent(prev => prev ? `${prev} ${processedText}` : processedText);
      setInputMethod('voice');
      setVoiceConfidence(confidence);
    }
  });

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [content, interimTranscript]);

  // Hantera submit
  const handleSubmit = async () => {
    const trimmedContent = content.trim();
    if (!trimmedContent || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onSubmit({
        content: trimmedContent,
        inputMethod,
        voiceConfidence: inputMethod === 'voice' ? voiceConfidence : undefined,
        visibility,
        isActionPoint,
        room: room.trim() || undefined
      });

      // Rensa fältet efter lyckad submit (room behålls — sticky)
      setContent('');
      setInputMethod('text');
      setVoiceConfidence(0);
      setIsActionPoint(false);

      // Fokusera på textfältet
      textareaRef.current?.focus();
    } catch (err) {
      console.error('Fel vid sparande av anteckning:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Hantera Enter för submit (Shift+Enter för ny rad)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Kombinerat innehåll med interim transcript
  const displayContent = isListening && interimTranscript
    ? content + (content ? ' ' : '') + interimTranscript
    : content;

  return (
    <div className="border rounded-lg bg-white shadow-sm">
      {/* Textarea */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={displayContent}
          onChange={(e) => {
            setContent(e.target.value);
            setInputMethod('text');
          }}
          onKeyDown={handleKeyDown}
          placeholder={isListening ? 'Lyssnar...' : placeholder}
          disabled={disabled || isListening}
          autoFocus={autoFocus}
          rows={1}
          className={`w-full px-3 py-2 text-sm resize-none border-0 focus:ring-0 focus:outline-none
            ${isListening ? 'bg-red-50' : ''}
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
          style={{ minHeight: '40px', maxHeight: '120px' }}
        />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between px-2 py-1.5 border-t bg-gray-50">
        {/* Vänster: Röst + synlighet */}
        <div className="flex items-center gap-2">
          <VoiceRecorder
            isListening={isListening}
            isSupported={isSupported}
            error={voiceError}
            interimTranscript={interimTranscript}
            onStart={startListening}
            onStop={stopListening}
            disabled={disabled}
            size="sm"
          />

          {/* Synlighets-toggle */}
          <button
            onClick={() => setVisibility(v => v === 'internal' ? 'shared' : 'internal')}
            disabled={disabled}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors
              ${visibility === 'shared'
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }
              disabled:opacity-50 disabled:cursor-not-allowed`}
            title={visibility === 'shared'
              ? 'Delad med alla mötesdeltagare'
              : 'Endast synlig för dig'
            }
          >
            {visibility === 'shared' ? (
              <>
                <GlobeAltIcon className="w-3.5 h-3.5" />
                <span>Delad</span>
              </>
            ) : (
              <>
                <LockClosedIcon className="w-3.5 h-3.5" />
                <span>Privat</span>
              </>
            )}
          </button>

          {/* Actionpunkt-toggle */}
          <button
            onClick={() => setIsActionPoint(v => !v)}
            disabled={disabled}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors
              ${isActionPoint
                ? 'bg-leadme-pink/20 text-leadme-pink hover:bg-leadme-pink/30'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }
              disabled:opacity-50 disabled:cursor-not-allowed`}
            title={isActionPoint
              ? 'Blir en actionpunkt i protokollet'
              : 'Gör till actionpunkt'
            }
          >
            <ClipboardCheckIcon className="w-3.5 h-3.5" />
            <span>Action</span>
          </button>

          {/* Rum/kategori — sticky mellan anteckningar */}
          <input
            type="text"
            value={room}
            onChange={(e) => setRoom(e.target.value)}
            placeholder="Rum..."
            disabled={disabled}
            className="w-20 px-2 py-1 text-xs border border-gray-200 rounded bg-white placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary disabled:opacity-50"
            title="Rum/kategori (t.ex. Badrum, Kök) — behålls för nästa anteckning"
          />

          {/* Röstindikator */}
          {inputMethod === 'voice' && voiceConfidence > 0 && (
            <span className="text-xs text-gray-400">
              {Math.round(voiceConfidence * 100)}% säkerhet
            </span>
          )}
        </div>

        {/* Höger: Skicka-knapp */}
        <button
          onClick={handleSubmit}
          disabled={disabled || isSubmitting || !content.trim()}
          className={`p-1.5 rounded-full transition-colors
            ${content.trim()
              ? 'bg-leadme-pink text-white hover:bg-pink-600'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
            disabled:opacity-50 disabled:cursor-not-allowed`}
          title="Spara anteckning"
        >
          <PaperAirplaneIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default NoteInput;
